// ============================================================================
// 覚醒王 DEVプロキシ (2026-07-07 Fable5 ビルド) — ⚠️これは本番アートではない ⚠️
//
// 目的: 最終キャラ絵が無くても戦闘設計を止めないための「読める仮モデル」。
//   頭・顎(上下)・胴・前後肢・砕けた冠・鎖の名残 を持つ四足獣をパラメトリックに描き、
//   ポーズごとに offscreen canvas → dataURL 画像として生成する。
//
// なぜ dataURL 注入方式か (docs/reports/2026-07-07-awoken-king-fable5-build-state.md D5):
//   生成画像を既存スプライトキャッシュ (sprites.ts) に流すことで、面積正規化・接地アンカー・
//   crossfade/hardcut・パリィrecoil・命中フラッシュ・赤オーラ・影・撃破フェード = 全ボス共通の
//   描画/演出経路を 1 行も変えずにそのまま通る (カスタム描画ボスが共通演出を取りこぼす既知の
//   故障型 = feedback_boss_render_common_path_audit の構造的回避)。
//
// 最終絵への差し替え手順 (戦闘ロジックは不変・2026-07-07 独立レビュー反映で4点追加):
//   1. public/sprites/bosses/crowned-king-awoken/ へ同名 PNG (下の POSE_KEYS 一覧) を置く
//      — 発注時に「左向き」を明記 (SOURCE_FACING['crowned-king-awoken']='left'。右向き納品なら
//        sprites.ts の同テーブルを 'right' へ更新しないと全ポーズ反転する)
//   2. boss_proxy.ts の setBossProxyEnabled('crowned-king-awoken', false) で OFF にする
//      (実行中の切替は __bossProxy('crowned-king-awoken', false) — こちらは
//      clearBossSpriteCache + preload まで内部でやる)
//   3. 最終 idle.png の bbox 高さを実測し (scripts/awoken_measure_bbox.mjs 参照)、
//      sprites.ts の BOSS_FIXED_SCALE_REF_H['crowned-king-awoken'] (現在 421=プロキシ実測) を更新
//   4. ポーズ間で体格が跳ねる絵は POSE_SCALE_FIX['crowned-king-awoken'] で個別補正
//      (花嫁/処刑人の前例。現状プロキシは同一骨格なので空)
//   5. boss_pose_timeline.ts / attacks.ts / bosses.ts は触らない
// ============================================================================

export const AWOKEN_PROXY_BOSS_ID = 'crowned-king-awoken'

// 🔴 2026-08-07: 既定 OFF へ。本番のキーポーズ絵 27枚が
//   public/sprites/bosses/crowned-king-awoken/ に入ったため (上の手順1〜4を実施済)。
//   ~~既定 ON (旧マスター6枚より proxy を優先)~~ は本番絵が無かった間の措置。
//   proxy のコードは残置 = 絵を疑った時に __bossProxy('crowned-king-awoken', true) で比較できる。
//   有効状態は boss_proxy.ts、DEV トグルは main.ts の __bossProxy()。

// ---------------------------------------------------------------------------
// ポーズのパラメータ表現。 座標はすべて 960x760 キャンバス・左向き (SOURCE_FACING と一致)。
//   接地ライン y=720。 面積正規化 (sprites.ts) が中身 bbox 基準でスケールするので、
//   ポーズ間は「同じ体の部品を動かすだけ」にしてサイズ一貫性を保つ。
// ---------------------------------------------------------------------------
type XY = [number, number]
type AKPose = {
  shoulder: XY          // 肩 (胸) の位置
  hip: XY               // 腰の位置
  head: XY              // 頭蓋の中心位置
  headAngle?: number    // 頭の傾き (rad・負=上向き)
  jaw?: number          // 顎の開き 0..1
  arch?: number         // 背のアーチ (制御点の持ち上げ px・負=山なり)
  // 脚: 足先の位置 (nf=前近/ff=前遠/nh=後近/fh=後遠)。 遠側は描画時に +18px 奥へずらす
  nf: XY; ff: XY; nh: XY; fh: XY
  frontBend?: number    // 前肢の膝の張り出し (px・正=後方へ)
  hindBend?: number     // 後肢の飛節の張り出し (px・正=前方へ)
}

const P = (p: AKPose): AKPose => p

// 26 ポーズ。 komawari パック §2 の「読める層+動く層」を DEV 品質で全部持つ。
export const AWOKEN_PROXY_POSES: Record<string, AKPose> = {
  // ---- 非攻撃 ----
  idle: P({ shoulder: [330, 430], hip: [620, 415], head: [205, 415], headAngle: 0.08, jaw: 0.15,
    nf: [300, 718], ff: [368, 718], nh: [600, 718], fh: [668, 718] }),
  walk_passing: P({ shoulder: [330, 420], hip: [620, 410], head: [200, 405], headAngle: 0.05, jaw: 0.1,
    nf: [330, 712], ff: [310, 700], nh: [640, 712], fh: [610, 702] }),
  walk_reach: P({ shoulder: [325, 435], hip: [625, 420], head: [195, 415], headAngle: 0.06, jaw: 0.12,
    nf: [225, 715], ff: [400, 712], nh: [545, 715], fh: [720, 710] }),
  parried: P({ shoulder: [365, 425], hip: [615, 430], head: [290, 340], headAngle: -0.55, jaw: 0.9,
    arch: 26, nf: [330, 716], ff: [395, 716], nh: [610, 718], fh: [672, 718] }),
  staggered_enter: P({ shoulder: [340, 540], hip: [620, 520], head: [235, 560], headAngle: 0.35, jaw: 0.6,
    nf: [235, 716], ff: [420, 714], nh: [545, 716], fh: [710, 714], frontBend: 40 }),
  staggered_hold: P({ shoulder: [350, 585], hip: [630, 560], head: [245, 640], headAngle: 0.55, jaw: 0.8,
    nf: [225, 718], ff: [440, 716], nh: [540, 718], fh: [720, 716], frontBend: 55, hindBend: 40 }),
  defeat: P({ shoulder: [350, 645], hip: [630, 630], head: [225, 690], headAngle: 0.5, jaw: 0.2,
    nf: [240, 720], ff: [430, 718], nh: [540, 720], fh: [720, 718], frontBend: 60, hindBend: 55 }),
  // 汎用 state 絵 (タイムライン外のフォールバック): 意味の近いポーズの別名
  telegraph: P({ shoulder: [330, 468], hip: [620, 450], head: [250, 470], headAngle: 0.18, jaw: 0.85,
    nf: [280, 718], ff: [385, 718], nh: [590, 718], fh: [680, 718] }),
  active: P({ shoulder: [290, 440], hip: [640, 428], head: [130, 430], headAngle: 0.05, jaw: 0.15,
    nf: [225, 715], ff: [330, 715], nh: [610, 716], fh: [700, 714] }),
  staggered: P({ shoulder: [350, 585], hip: [630, 560], head: [245, 640], headAngle: 0.55, jaw: 0.8,
    nf: [225, 718], ff: [440, 716], nh: [540, 718], fh: [720, 716], frontBend: 55, hindBend: 40 }),

  // ---- 暴噛ファミリー ----
  // ★ポーズ語彙 (2026-07-07 第1回パック回収で全面改訂): 派生サインは「既習の大シルエット動詞」で出す。
  //   沈む=単発噛み / 立ち上がる=連撃 / 弓なり水平=突進 / 玉に縮む=跳躍 / 頭が床=本物の隙 /
  //   頭が跳ね上がる=まだ来る / 二段沈み=溜め。 微妙な姿勢差は1倍速で読めない (ユーザー実証)。
  bite_windup: P({ shoulder: [335, 470], hip: [620, 448], head: [252, 468], headAngle: 0.2, jaw: 0.85,
    nf: [285, 718], ff: [388, 718], nh: [588, 718], fh: [678, 718] }),
  // 暴噛・溜めの二段目: さらに深く沈む (=「まだ来ない」を体で読ませる。A-5 裁定=構えも変える)
  bite_windup_deep: P({ shoulder: [348, 512], hip: [612, 482], head: [262, 515], headAngle: 0.32, jaw: 0.95,
    nf: [272, 718], ff: [400, 718], nh: [572, 718], fh: [668, 718], frontBend: 34, hindBend: 42 }),
  bite_contact: P({ shoulder: [288, 438], hip: [645, 428], head: [128, 428], headAngle: 0.04, jaw: 0.12,
    nf: [222, 715], ff: [328, 715], nh: [612, 716], fh: [702, 714] }),
  // 噛んだ勢いのまま前脚が前へ着いた瞬間 (2026-09-08 追加)。滑り対策で active 後半に挟む絵。
  //   本番絵 = bite_step.png。ここは DEV プロキシなので「前脚が bite_contact より前で接地」だけ satisfies すればよい。
  bite_step: P({ shoulder: [292, 448], hip: [648, 430], head: [140, 442], headAngle: 0.06, jaw: 0.15,
    nf: [185, 716], ff: [305, 715], nh: [628, 716], fh: [716, 714] }),
  bite_follow: P({ shoulder: [300, 460], hip: [640, 425], head: [148, 505], headAngle: 0.3, jaw: 0.05,
    nf: [235, 716], ff: [345, 716], nh: [615, 716], fh: [700, 715] }),
  bite_recover: P({ shoulder: [332, 442], hip: [622, 420], head: [228, 400], headAngle: -0.02, jaw: 0.2,
    nf: [298, 718], ff: [370, 718], nh: [602, 718], fh: [670, 718] }),

  // ---- 暴連噛: 「立ち上がる=連撃が来る」の動詞 (単発の「沈む」と真逆の大シルエット) ----
  frenzy_open: P({ shoulder: [370, 348], hip: [628, 468], head: [305, 258], headAngle: -0.6, jaw: 1.0,
    arch: 18, nf: [305, 475], ff: [378, 500], nh: [582, 718], fh: [666, 718], hindBend: 40 }),
  frenzy_bite2: P({ shoulder: [285, 460], hip: [648, 430], head: [135, 478], headAngle: 0.22, jaw: 0.25,
    nf: [218, 714], ff: [330, 714], nh: [614, 716], fh: [704, 714] }),
  // 伸びのサイン: 息継ぎ (bite_follow=頭が流れる) と正反対の「もう一度 噛みの構え (沈む) に入る」
  //   = 既習の bite_windup 語彙の再登場。 学んだ人には一目で「まだ来る」。
  extend_coil: P({ shoulder: [345, 488], hip: [615, 500], head: [255, 488], headAngle: 0.24, jaw: 0.9,
    nf: [285, 718], ff: [390, 718], nh: [552, 718], fh: [635, 718], frontBend: 30, hindBend: 55 }),

  // ---- 暴走突進 / 追い噛み ----
  rush_windup: P({ shoulder: [402, 468], hip: [612, 442], head: [332, 452], headAngle: 0.1, jaw: 0.6,
    arch: -30, nf: [372, 718], ff: [448, 718], nh: [566, 718], fh: [648, 718], hindBend: 46 }),
  rush_lunge: P({ shoulder: [278, 428], hip: [682, 422], head: [118, 418], headAngle: 0.02, jaw: 0.7,
    nf: [178, 598], ff: [258, 622], nh: [742, 636], fh: [806, 658] }),
  // 本物の隙: 鼻先が床に刺さるまで深く突っ伏す (「頭が床=殴ってよし」の動詞を最大化)
  rush_overextend: P({ shoulder: [305, 508], hip: [618, 418], head: [152, 618], headAngle: 0.8, jaw: 0.05,
    nf: [182, 714], ff: [335, 712], nh: [598, 716], fh: [686, 714], frontBend: 45 }),
  // 追い噛みのサイン: 突っ伏すはずの頭が「跳ね上がって牙を剥く」= 本物と正反対の縦方向
  chase_coil: P({ shoulder: [328, 438], hip: [612, 448], head: [196, 380], headAngle: -0.55, jaw: 1.0,
    nf: [255, 715], ff: [355, 715], nh: [552, 718], fh: [630, 718], hindBend: 50 }),

  // ---- 大暴れ: 「玉に縮む=跳ぶ」(連撃の立ち上がりと混同しない・跳躍の物理予備動作) ----
  rampage_leap: P({ shoulder: [372, 528], hip: [598, 512], head: [285, 512], headAngle: 0.15, jaw: 0.45,
    nf: [330, 718], ff: [400, 718], nh: [540, 718], fh: [610, 718], frontBend: 42, hindBend: 55 }),
  rampage_slam: P({ shoulder: [318, 502], hip: [622, 402], head: [218, 522], headAngle: 0.4, jaw: 0.3,
    nf: [248, 718], ff: [352, 718], nh: [598, 705], fh: [676, 702], frontBend: 26 }),
  slam_settle: P({ shoulder: [330, 492], hip: [622, 470], head: [228, 482], headAngle: 0.18, jaw: 0.2,
    nf: [275, 718], ff: [372, 718], nh: [578, 718], fh: [662, 718], frontBend: 30, hindBend: 30 }),

  // ---- 壁跳ね ----
  dash_crouch: P({ shoulder: [338, 502], hip: [642, 492], head: [218, 490], headAngle: 0.06, jaw: 0.4,
    nf: [292, 718], ff: [382, 718], nh: [575, 718], fh: [655, 718], frontBend: 36, hindBend: 42 }),
  dash_form: P({ shoulder: [275, 445], hip: [688, 438], head: [108, 438], headAngle: 0.0, jaw: 0.55,
    nf: [160, 590], ff: [246, 618], nh: [762, 622], fh: [828, 648] }),
  wall_kick: P({ shoulder: [398, 548], hip: [628, 352], head: [298, 520], headAngle: 0.28, jaw: 0.5,
    arch: 20, nf: [368, 648], ff: [442, 662], nh: [758, 388], fh: [788, 455], hindBend: -30 }),

  // ---- 大暴れ第5案の本番絵 (2026-09-20 発注) の仮姿 ----
  //   プロキシは絵が届くまでの代役なので、近い既存ポーズを口と後肢だけ変えて流用する。
  //   swoop_bite = 水平に伸びて噛む (rush_lunge + 口を全開)、slam_landing = 前肢を床へ刺し後肢はまだ宙。
  swoop_bite_0920: P({ shoulder: [278, 428], hip: [682, 422], head: [118, 418], headAngle: -0.05, jaw: 1.0,
    nf: [178, 598], ff: [258, 622], nh: [742, 636], fh: [806, 658] }),
  slam_landing_0920: P({ shoulder: [318, 512], hip: [628, 366], head: [222, 556], headAngle: 0.45, jaw: 0.5,
    nf: [246, 718], ff: [348, 718], nh: [600, 612], fh: [682, 596], frontBend: 22, hindBend: -20 }),

  // ---- 咆哮 / 致命 ----
  roar_up: P({ shoulder: [362, 300], hip: [638, 478], head: [332, 182], headAngle: -0.95, jaw: 1.0,
    arch: 22, nf: [298, 428], ff: [378, 458], nh: [575, 718], fh: [665, 718] }),
  exec_windup: P({ shoulder: [355, 315], hip: [635, 470], head: [278, 295], headAngle: -0.2, jaw: 0.95,
    arch: 20, nf: [290, 445], ff: [368, 468], nh: [578, 718], fh: [668, 718] }),
  exec_bite: P({ shoulder: [302, 522], hip: [622, 420], head: [168, 622], headAngle: 0.55, jaw: 0.08,
    nf: [212, 718], ff: [335, 718], nh: [600, 712], fh: [682, 710], frontBend: 30 }),
}

// The adopted teeth variants keep their original body's proxy pose.
AWOKEN_PROXY_POSES.rush_windup_fangs_0917 = AWOKEN_PROXY_POSES.rush_windup!
AWOKEN_PROXY_POSES.rush_windup_fangs_1007 = AWOKEN_PROXY_POSES.rush_windup!
AWOKEN_PROXY_POSES.chase_coil_fangs_0924 = AWOKEN_PROXY_POSES.chase_coil!
AWOKEN_PROXY_POSES.chase_coil_fangs_1001 = AWOKEN_PROXY_POSES.chase_coil!
export const AWOKEN_PROXY_POSE_KEYS: readonly string[] = Object.keys(AWOKEN_PROXY_POSES)
export function isAwokenProxyPose(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(AWOKEN_PROXY_POSES, key)
}

// ---------------------------------------------------------------------------
// 描画: 台帳の見た目ピン準拠 = 黒い大型獣 + 砕けた金冠 (棘3-5本・1本折れ) + 顔の紫の虚無 +
//   引きちぎれた鎖の名残1本 (前肢に密着・独立して揺れない)。 武器なし。
//   オーラ・火の粉・床の滲みは絵に描かない (全部コードFX = render 側の共通経路)。
//   装飾密度ルール: 大シルエット1 (獣体) + 装飾2 (冠+鎖) + 揺れ装飾0。
// ---------------------------------------------------------------------------
const CANVAS_W = 960
const CANVAS_H = 760

const COL = {
  bodyDark: '#161020',   // 紫黒の獣体
  bodyMid: '#1e1630',
  limbFar: '#100b18',
  ridgeHi: '#4a3a68',    // 背/頭頂の淡いリム (暗背景での輪郭可読性)
  crown: '#8a763d',      // 鈍い金 (砕けた冠)
  crownDark: '#57492a',
  chain: '#45414d',      // 鉄鎖の名残
  chainHi: '#5c5868',
  fang: '#b9b2c4',
  faceVoid: '#2b1d38',   // 顔の虚無の下地 (発光は render 側の魂グローが担う)
} as const

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t }

// 2セグメント脚: 付け根→膝→足先。 bend = 膝の張り出し (脚線に垂直方向)。
const LEG_W_NEAR_FRONT = 52
const LEG_W_NEAR_HIND = 58
const LEG_W_FAR = 44
function drawLeg(
  ctx: CanvasRenderingContext2D, rootX: number, rootY: number, foot: XY,
  bend: number, width: number, color: string,
): void {
  const [fx, fy] = foot
  const mx = (rootX + fx) / 2
  const my = (rootY + fy) / 2
  // 脚線に垂直な方向 (左向きの獣: 正の bend = 進行方向の逆=右へ張る)
  const dx = fx - rootX, dy = fy - rootY
  const len = Math.max(1, Math.hypot(dx, dy))
  const px = -dy / len, py = dx / len
  const kx = mx + px * bend, ky = my + py * bend
  ctx.strokeStyle = color
  ctx.lineWidth = width
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(rootX, rootY)
  ctx.quadraticCurveTo(kx, ky, fx, fy)
  ctx.stroke()
  // 足先の爪 (前方向へ小さな三角)
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.moveTo(fx - width * 0.55, fy)
  ctx.lineTo(fx + width * 0.25, fy - width * 0.28)
  ctx.lineTo(fx + width * 0.25, fy + width * 0.1)
  ctx.closePath()
  ctx.fill()
}

function drawPoseInto(ctx: CanvasRenderingContext2D, pose: AKPose): void {
  const [sx, sy] = pose.shoulder
  const [hx, hy] = pose.hip
  const [hdx, hdy] = pose.head
  const jaw = pose.jaw ?? 0.2
  const headAngle = pose.headAngle ?? 0
  const arch = pose.arch ?? 0
  const frontBend = pose.frontBend ?? 18
  const hindBend = pose.hindBend ?? 26

  // --- 遠側の脚 (体の奥・暗い) ---
  drawLeg(ctx, sx + 18, sy + 22, [pose.ff[0] + 18, pose.ff[1]], frontBend, LEG_W_FAR, COL.limbFar)
  drawLeg(ctx, hx + 18, hy + 26, [pose.fh[0] + 18, pose.fh[1]], -hindBend, LEG_W_FAR, COL.limbFar)

  // --- 胴 (肩→腰の太いカプセル + 背のアーチ) ---
  const midX = (sx + hx) / 2
  const midY = (sy + hy) / 2 - 26 + arch
  ctx.strokeStyle = COL.bodyDark
  ctx.lineWidth = 118
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(sx, sy)
  ctx.quadraticCurveTo(midX, midY, hx, hy)
  ctx.stroke()
  // 胸の深さ (前半身を一回り太く = 獣の質量を前に置く)
  ctx.lineWidth = 148
  ctx.beginPath()
  ctx.moveTo(sx + 8, sy + 4)
  ctx.lineTo(lerp(sx, hx, 0.3), lerp(sy, hy, 0.3) + 2)
  ctx.stroke()
  // 背のリムライト (輪郭可読性・発光ではない)
  ctx.strokeStyle = COL.ridgeHi
  ctx.globalAlpha = 0.55
  ctx.lineWidth = 5
  ctx.beginPath()
  ctx.moveTo(sx - 8, sy - 56)
  ctx.quadraticCurveTo(midX, midY - 62, hx + 14, hy - 52)
  ctx.stroke()
  ctx.globalAlpha = 1
  // 背の逆立つ体毛 (シルエットの荒れ = 暴走の名残。 揺れない=焼き込み)
  ctx.fillStyle = COL.bodyDark
  for (let i = 0; i < 5; i++) {
    const t = 0.15 + i * 0.17
    const bx = lerp(sx, hx, t)
    const by = lerp(sy, hy, t) - 52 + arch * (1 - Math.abs(t - 0.5) * 2) * 0.8
    ctx.beginPath()
    ctx.moveTo(bx - 14, by + 6)
    ctx.lineTo(bx + 2 - i, by - 22 - (i % 2) * 8)
    ctx.lineTo(bx + 16, by + 6)
    ctx.closePath()
    ctx.fill()
  }
  // 尻の短い尾 (千切れた房・体に密着)
  ctx.strokeStyle = COL.bodyDark
  ctx.lineWidth = 26
  ctx.beginPath()
  ctx.moveTo(hx + 30, hy - 20)
  ctx.quadraticCurveTo(hx + 78, hy - 6, hx + 66, hy + 32)
  ctx.stroke()

  // --- 近側の後肢 ---
  drawLeg(ctx, hx - 6, hy + 30, pose.nh, -hindBend, LEG_W_NEAR_HIND, COL.bodyMid)

  // --- 首 (肩→頭・テーパー2段) ---
  const nbx = lerp(sx, hdx, 0.35)
  const nby = lerp(sy, hdy, 0.35)
  ctx.strokeStyle = COL.bodyDark
  ctx.lineCap = 'round'
  ctx.lineWidth = 86
  ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(nbx, nby); ctx.stroke()
  ctx.lineWidth = 60
  ctx.beginPath(); ctx.moveTo(nbx, nby); ctx.lineTo(hdx + 18, hdy + 4); ctx.stroke()

  // --- 近側の前肢 (首の上に重ねる=手前) ---
  drawLeg(ctx, sx - 6, sy + 26, pose.nf, frontBend, LEG_W_NEAR_FRONT, COL.bodyMid)

  // --- 鎖の名残 (装飾2/2): 近側前肢の上腕 (体のすぐ下) に密着して巻き付く。 自由端は描かない ---
  {
    const rx = sx - 6, ry = sy + 26
    const [fx2, fy2] = pose.nf
    const ang = Math.atan2(fy2 - ry, fx2 - rx) + Math.PI / 2
    for (let i = 0; i < 3; i++) {
      const t = 0.3 + i * 0.09   // 上腕 (体の際から少し下) に3リンク
      const cx = lerp(rx, fx2, t)
      const cy = lerp(ry, fy2, t)
      ctx.strokeStyle = i % 2 === 0 ? COL.chain : COL.chainHi
      ctx.lineWidth = 6
      ctx.beginPath()
      ctx.ellipse(cx, cy, LEG_W_NEAR_FRONT * 0.62, 9, ang, 0, Math.PI * 2)
      ctx.stroke()
    }
  }

  // --- 頭 (頭蓋 + 下顎 + 牙 + 冠 + 顔の虚無) ---
  ctx.save()
  ctx.translate(hdx, hdy)
  ctx.rotate(headAngle)
  // 上頭蓋 (左=吻先)
  ctx.fillStyle = COL.bodyDark
  ctx.beginPath()
  ctx.moveTo(46, -40)          // 後頭部の上
  ctx.quadraticCurveTo(-20, -46, -62, -22)   // 額→鼻梁
  ctx.lineTo(-102, -4)         // 吻先の上
  ctx.lineTo(-104, 8)          // 吻先
  ctx.lineTo(-30, 16)          // 上顎ライン
  ctx.lineTo(30, 22)           // 顎関節へ
  ctx.quadraticCurveTo(52, 4, 46, -40)
  ctx.closePath()
  ctx.fill()
  // 頭頂のリム
  ctx.strokeStyle = COL.ridgeHi
  ctx.globalAlpha = 0.5
  ctx.lineWidth = 4
  ctx.beginPath()
  ctx.moveTo(40, -40)
  ctx.quadraticCurveTo(-22, -46, -62, -22)
  ctx.stroke()
  ctx.globalAlpha = 1
  // 耳 (千切れた短い三角・揺れない)
  ctx.fillStyle = COL.bodyDark
  ctx.beginPath(); ctx.moveTo(30, -36); ctx.lineTo(44, -62); ctx.lineTo(54, -34); ctx.closePath(); ctx.fill()
  // 上牙 (吻先の下向き)
  ctx.fillStyle = COL.fang
  ctx.beginPath(); ctx.moveTo(-96, 8); ctx.lineTo(-90, 26); ctx.lineTo(-84, 8); ctx.closePath(); ctx.fill()
  ctx.beginPath(); ctx.moveTo(-72, 12); ctx.lineTo(-67, 26); ctx.lineTo(-61, 12); ctx.closePath(); ctx.fill()
  // 下顎 (jaw 0..1 で顎関節 (26, 20) 周りに開く)
  ctx.save()
  ctx.translate(26, 20)
  ctx.rotate(jaw * 0.52)
  ctx.fillStyle = COL.bodyMid
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.lineTo(-116, 4)
  ctx.lineTo(-118, 20)
  ctx.quadraticCurveTo(-40, 34, 4, 22)
  ctx.closePath()
  ctx.fill()
  // 下牙 (上向き)
  ctx.fillStyle = COL.fang
  ctx.beginPath(); ctx.moveTo(-108, 6); ctx.lineTo(-102, -10); ctx.lineTo(-96, 5); ctx.closePath(); ctx.fill()
  ctx.beginPath(); ctx.moveTo(-84, 7); ctx.lineTo(-79, -7); ctx.lineTo(-73, 7); ctx.closePath(); ctx.fill()
  ctx.restore()
  // 顔の虚無 (下地のみ。 紫の発光は render の魂グロー = 絵に光を焼かない)
  ctx.fillStyle = COL.faceVoid
  ctx.beginPath()
  ctx.ellipse(-34, -14, 26, 15, -0.12, 0, Math.PI * 2)
  ctx.fill()
  // 砕けた金冠 (装飾1/2): 頭頂に棘4本・1本は折れて短い
  const spikes: Array<[number, number, number]> = [
    [22, -40, 34],   // [x, 基部y, 高さ]
    [4, -43, 46],
    [-14, -44, 40],
    [-32, -41, 16],  // ← 折れた棘
  ]
  for (const [bx, by, h] of spikes) {
    ctx.fillStyle = COL.crown
    ctx.strokeStyle = COL.crownDark
    ctx.lineWidth = 2.5
    ctx.beginPath()
    ctx.moveTo(bx - 8, by)
    // 折れた棘は先端をギザギザに
    if (h <= 20) {
      ctx.lineTo(bx - 4, by - h)
      ctx.lineTo(bx, by - h + 6)
      ctx.lineTo(bx + 4, by - h - 2)
    } else {
      ctx.lineTo(bx, by - h)
    }
    ctx.lineTo(bx + 8, by)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
  }
  // 冠の帯
  ctx.fillStyle = COL.crownDark
  ctx.fillRect(-40, -44, 70, 7)
  ctx.restore()
}

// ---------------------------------------------------------------------------
// dataURL 生成 + キャッシュ。 document が無い環境 (vitest/node) では null を返し、
//   呼び出し側 (sprites.ts) は従来のファイルロードへフォールバックする。
// ---------------------------------------------------------------------------
const dataUrlCache = new Map<string, string>()

export function getAwokenProxyDataUrl(key: string): string | null {
  const pose = AWOKEN_PROXY_POSES[key]
  if (!pose) return null
  if (typeof document === 'undefined') return null
  const cached = dataUrlCache.get(key)
  if (cached) return cached
  const canvas = document.createElement('canvas')
  canvas.width = CANVAS_W
  canvas.height = CANVAS_H
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  drawPoseInto(ctx, pose)
  const url = canvas.toDataURL('image/png')
  dataUrlCache.set(key, url)
  return url
}

// DEV ラボ用: ポーズ2つのパラメータを線形補間して描く (リグ的な「補間で動かす」見え方の実証)。
//   本番経路では使わない — アニメ方式比較 (awoken-proxy-lab.html) 専用。
export function drawAwokenProxyLerp(
  ctx: CanvasRenderingContext2D, a: string, b: string, t: number,
): void {
  const pa = AWOKEN_PROXY_POSES[a]
  const pb = AWOKEN_PROXY_POSES[b]
  if (!pa || !pb) return
  const lerpXY = (u: XY, v: XY): XY => [lerp(u[0], v[0], t), lerp(u[1], v[1], t)]
  const mixed: AKPose = {
    shoulder: lerpXY(pa.shoulder, pb.shoulder),
    hip: lerpXY(pa.hip, pb.hip),
    head: lerpXY(pa.head, pb.head),
    headAngle: lerp(pa.headAngle ?? 0, pb.headAngle ?? 0, t),
    jaw: lerp(pa.jaw ?? 0.2, pb.jaw ?? 0.2, t),
    arch: lerp(pa.arch ?? 0, pb.arch ?? 0, t),
    nf: lerpXY(pa.nf, pb.nf), ff: lerpXY(pa.ff, pb.ff),
    nh: lerpXY(pa.nh, pb.nh), fh: lerpXY(pa.fh, pb.fh),
    frontBend: lerp(pa.frontBend ?? 18, pb.frontBend ?? 18, t),
    hindBend: lerp(pa.hindBend ?? 26, pb.hindBend ?? 26, t),
  }
  drawPoseInto(ctx, mixed)
}

export const AWOKEN_PROXY_CANVAS = { w: CANVAS_W, h: CANVAS_H } as const
